import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface ValidationRule {
  type: 'required' | 'email' | 'minLength' | 'maxLength' | 'pattern' | 'custom';
  value?: any;
  message: string | ((value: string) => string);
}

/**
 * Reusable custom input component that integrates with Angular forms (ControlValueAccessor), supports multiple input types, custom suffix templates, and flexible validation rules. It manages internal touched/disabled state, displays validation messages when enabled, and emits value/blur events for external handling.
 * Key methods: writeValue, registerOnChange, registerOnTouched, setDisabledState, onInputChange, onBlur, validateField (and a private validateRule helper). The component uses ngOnInit to initialize any provided initial value.
 * @component
 * @selector app-custom-input
 * @example
 *   <app-custom-input
 *     [label]="'Email'"
 *     [placeholder]="'Enter email'"
 *     [type]="'email'"
 *     [required]="true"
 *     [validationRules]="emailRules"
 *     [showValidation]="true"
 *     [(value)]="model.email"
 *     (blur)="onEmailBlur()">
 *   </app-custom-input>
 * @input string label - The text label displayed alongside the input.
 * @input string placeholder - Placeholder text shown inside the input when empty.
 * @input 'text'|'email'|'tel'|'password'|'number' type - Input type controlling keyboard/format and basic behavior.
 * @input boolean required - When true, applies required validation rule to the input.
 * @input boolean disabled - When true, disables user interaction and applies disabled styling.
 * @input ValidationRule[] validationRules - Array of validation rules (e.g., required, email, minLength, maxLength, pattern, custom function) used by validateField.
 * @input boolean showValidation - If true, validation is performed and error messages are surfaced after touch or change.
 * @input boolean markTouchedOnInput - If true with showValidation, first input marks the field touched so errors can show before blur.
 * @input string value - The current input value (supports two-way binding via valueChange).
 * @input any suffixTemplate - Optional template/content rendered as a suffix (icons, buttons, etc.).
 * @output EventEmitter<string> valueChange - Emits the current value whenever the input changes.
 * @output EventEmitter<void> blur - Emits when the input loses focus (useful for form interactions and marking touched).
 */
@Component({
  selector: 'app-custom-input',
  standalone: true,
  imports: [CommonModule, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomInputComponent),
      multi: true,
    },
  ],
  templateUrl: './custom-input.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./custom-input.component.scss'],
})
export class CustomInputComponent implements OnInit, ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() type: 'text' | 'email' | 'tel' | 'password' | 'number' = 'text';
  @Input() required = false;
  @Input() disabled = false;
  @Input() validationRules: ValidationRule[] = [];
  @Input() showValidation = false;
  /** When true with showValidation, the first input event marks the field touched so errors can show before blur. */
  @Input() markTouchedOnInput = false;
  /** Overrides keyboard hint, e.g. `numeric` for payment phone fields. */
  @Input() inputMode: string | null = null;
  @Input() value = '';
  @Input() suffixTemplate: any = null; // For custom suffix content like buttons

  @Output() valueChange = new EventEmitter<string>();
  @Output() blur = new EventEmitter<void>();

  errorMessage = '';
  isTouched = false;

  private onChange = (value: string) => {};
  private onTouched = () => {};

  ngOnInit(): void {
    // Initialize with current value if provided
    if (this.value) {
      this.onChange(this.value);
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: string): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.value = target.value;
    this.onChange(this.value);
    this.valueChange.emit(this.value);
    if (this.showValidation && this.markTouchedOnInput) {
      this.isTouched = true;
    }
    this.validateField();
  }

  onBlur(): void {
    this.isTouched = true;
    this.onTouched();
    this.blur.emit();
    this.validateField();
  }

  /**
   * Validate the current field value against configured validationRules, set the first failing rule's message to errorMessage, and emit onChange only if the error state changed.
   * @example
   * alidateField()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} No return value.
   **/
  validateField(): void {
    if (!this.showValidation) return;

    const previousError = this.errorMessage;
    this.errorMessage = '';

    for (const rule of this.validationRules) {
      if (!this.validateRule(rule)) {
        this.errorMessage =
          typeof rule.message === 'function' ? rule.message(this.value) : rule.message;
        break;
      }
    }

    // Only emit change if error message actually changed to prevent unnecessary updates
    if (previousError !== this.errorMessage) {
      // Trigger change detection if needed
      this.onChange(this.value);
    }
  }

  /**
   * Validates the component's current value against a single validation rule.
   * @example
   * validateRule({ type: 'email' })
   * true
   * @param {{ValidationRule}} {{rule}} - The validation rule object to apply.
   * @returns {{boolean}} Whether the current value satisfies the given rule.
   **/
  private validateRule(rule: ValidationRule): boolean {
    switch (rule.type) {
      case 'required':
        return this.value.trim().length > 0;

      case 'email': {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(this.value);
      }

      case 'minLength':
        return this.value.length >= (rule.value || 0);

      case 'maxLength':
        return this.value.length <= (rule.value || 0);

      case 'pattern':
        if (rule.value instanceof RegExp) {
          return rule.value.test(this.value);
        }
        return true;

      case 'custom':
        // Custom validation function can be passed as rule.value
        if (typeof rule.value === 'function') {
          return rule.value(this.value);
        }
        return true;

      default:
        return true;
    }
  }

  get hasError(): boolean {
    return this.showValidation && this.errorMessage !== '' && this.isTouched;
  }

  get inputClasses(): string {
    let classes = 'custom-input-field';
    if (this.hasError) {
      classes += ' error';
    }
    if (this.disabled) {
      classes += ' disabled';
    }
    return classes;
  }
}
